import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import { useState, useEffect } from "react";
import { useAdviserContext } from "../../../context/Adviser/AdviserContext";
import { formatDate } from "../../../context/Adviser/Utils/FormatDate";
import { width } from "@mui/system";
import { alignProperty } from "@mui/material/styles/cssUtils";

/**
 * Renders a stacked bar chart visualizing an individual's work history.
 *
 * The chart displays each employment period as a stacked bar, divided into three segments:
 * - "Before Employment": Time before the job started (relative to the earliest job).
 * - "Employment Duration": Duration of the job.
 * - "After Employment": Time after the job ended (relative to the latest job).
 *
 * The chart is responsive and adjusts margins based on screen size and layout.
 * Tooltips provide detailed date information for each segment.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.workHistoryDocs - Array of work history objects, each containing at least `from`, `to`, and `nameOfFirm` fields. `from` and `to` can be Date objects, Firestore Timestamps, or ISO date strings.
 * @returns {JSX.Element} Stacked bar chart of work history.
 */
export default function WorkHistoryStackedBar() {
  const [layout, setLayout] = useState("horizontal");
  const [radius, setRadius] = useState(50);
  const [dataset, setDataset] = useState([]);
  const [globalMin, setGlobalMin] = useState(0);
  const { state } = useAdviserContext();
  const data = state?.data || {};
  const workHistoryRaw = data.workHistory || [];

  // normalize work history (array or object)
  const workHistoryDocs = (
    Array.isArray(workHistoryRaw)
      ? workHistoryRaw
      : Object.values(workHistoryRaw || {})
  ).filter(
    (job) => job?.currentEmploymentStatus?.toLowerCase() !== "unemployed"
  );

  // console.log(workHistoryDocs);

  useEffect(() => {
    if (!workHistoryDocs) return;

    const workHistory = Array.isArray(workHistoryDocs)
      ? workHistoryDocs
      : Object.values(workHistoryDocs || {});

    // Convert dates to epoch ms for math
    const allDates = workHistory
      .flatMap((job) => {
        const fromMs = toMs(job.from); // <-- number
        const toMsVal = toMs(job.to); // <-- number
        return [fromMs, toMsVal];
      })
      .filter(Number.isFinite); // keep only valid numbers

    if (!allDates.length) {
      // console.warn("No valid dates found");
      setDataset([]);
      setGlobalMin(0);
      return;
    }

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);

    // console.log("minDate:", new Date(minDate), "maxDate:", new Date(maxDate));

    setGlobalMin(minDate);

    const newDataset = workHistory
      .map((job, index) => {
        const fromMs = toMs(job.from);
        const toMsVal = toMs(job.to);

        if (!Number.isFinite(fromMs) || !Number.isFinite(toMsVal)) {
          // console.warn(`row ${index} skipped (invalid dates)`, job);
          return null;
        }

        return {
          order: `${index + 1}. ${job.nameOfFirm || "Unknown Firm"}`,
          before: Math.max(0, fromMs - minDate),
          during: Math.max(0, toMsVal - fromMs),
          after: Math.max(0, maxDate - toMsVal),
          from: dayjs(fromMs).format("DD-MM-YYYY"), // formatted for display
          to: dayjs(toMsVal).format("DD-MM-YYYY"),
        };
      })
      .filter(Boolean);

    // console.log("dataset:", newDataset);
    setDataset(newDataset);
  }, [data.workHistory]);

  // helper: normalize anything into ms
  const toMs = (v) => {
    if (!v) return NaN;
    if (v?.toDate) return v.toDate().getTime();
    if (typeof v === "object" && "seconds" in v) {
      return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    }
    if (v instanceof Date) return v.getTime();
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const d = dayjs(
        v,
        ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/YYYY", "YYYY"],
        true
      );
      return d.isValid() ? d.valueOf() : NaN;
    }
    return NaN;
  };

  const [responsiveMargin, setResponsiveMargin] = useState({
    left: 350,
    right: 300,
  });

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;

      if (layout === "vertical") {
        setResponsiveMargin({ left: 80 });
      } else if (screenWidth < 600) {
        setResponsiveMargin({ left: 100, right: 50 });
      } else if (screenWidth < 960) {
        setResponsiveMargin({ left: 200, right: 100 });
      } else {
        setResponsiveMargin({ left: 350, right: 300 });
      }
    };

    handleResize(); // Set initial margin
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [layout]);

  const BASE_WIDTH = 500;
  const PER_ITEM_WIDTH = 50;
  const chartWidth = BASE_WIDTH + dataset.length * PER_ITEM_WIDTH;

  const chartSettings = {
    dataset,
    height: 400,
    // margin: responsiveMargin,
    width: 1000,
    alignProperty: "center",

    xAxis: [
      {
        scaleType: "linear",
        // label: "Time",
        valueFormatter: (value) => dayjs(globalMin + value).format("YYYY"),
      },
    ],
    yAxis: [
      {
        scaleType: "band",
        dataKey: "order",
        categoryGapRatio: 0,
        barGapRatio: 0,
        // valueFormatter: (value, context) => (value === 0 ? "0" : value),
        valueFormatter: (value) => {
          if (!value) return "";
          const [idx, ...rest] = String(value).split(". ");
          const firm = rest.join(". "); // full firm name
          return `${firm.slice(0, 15)}..`;
        },
      },
    ],

    // Conditionally rotate the axis that has the 'band' scale

    // slotProps: {
    //   legend: {
    //     direction: "row",
    //     position: { vertical: "bottom", horizontal: "middle" },
    //     padding: -5,
    //   },
    // },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1200,
        overflowX: "auto",
        backgroundColor: "#ffffff",
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <BarChart
        data={dataset}
        colors={["#ffffff", "#9e145bff", "#ffffff"]}
        loading={dataset.length === 0}
        series={[
          {
            dataKey: "before",
            layout,
            ...dataset,
            stack: "total",
            // label: "Before Employment",
            // color: "#e9ebf8",
            // color: "#081e56",
            valueFormatter: (value) => {
              return dayjs(globalMin + value).format("MM-YYYY");
            },
          },
          {
            dataKey: "during",
            layout,
            stack: "total",
            label: "Employment Period",
            // color: "#9eb25d",
            valueFormatter: (value) => {
              return dayjs(globalMin + value).format("MM-YYYY");
            },
          },
          {
            dataKey: "after",
            layout,
            stack: "total",
            // label: "After Employment",
            // color: "#f1db4b",
            valueFormatter: (value) => {
              return dayjs(globalMin + value).format("MM-YYYY");
            },
          },
        ]}
        borderRadius={0}
        // borderRadius={radius}
        // onItemClick={(_, state,
        //   event,
        //   context,
        // )  => {
        //   console.log("Item clicked:", state, event, context);
        //   // console.log(state);
        // }}
        {...chartSettings}
        tooltip={{
          trigger: "none",
          render: (tooltipParams, state) => {
            const { data, series } = state;
            if (!data || !series) return null;

            const beforeStart = dayjs(data.from);
            const duringEnd = dayjs(data.to);
            const beforeEnd = beforeStart;
            const duringStart = beforeEnd;
            const afterStart = duringEnd;

            return (
              <div
                style={{
                  backgroundColor: "#f9fafc",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "8px",
                  color: "#333",
                  minWidth: 200,
                  fontFamily: "sans-serif",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    marginBottom: "5px",
                    color: "#081e56",
                  }}
                >
                  {data.order}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead
                    style={{
                      backgroundColor: "#081e56",
                      color: "white",
                      fontSize: "13px",
                      textAlign: "left",
                    }}
                  >
                    <tr>
                      <th style={{ padding: "4px" }}>Segment</th>
                      <th style={{ padding: "4px" }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series.map((item) => {
                      let label = item.label;
                      let date;

                      if (item.dataKey === "before") {
                        date = beforeStart;
                      } else if (item.dataKey === "during") {
                        date = duringStart;
                      } else if (item.dataKey === "after") {
                        date = afterStart;
                      }

                      return (
                        <tr key={item.label}>
                          <td
                            style={{
                              padding: "4px",
                              fontWeight: "bold",
                              color: item.color,
                            }}
                          >
                            {label}
                          </td>
                          <td style={{ padding: "4px", color: "#333" }}>
                            {date.format("MM-YYYY")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          },
        }}
      />
    </Box>
  );
}
